import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Card, Badge } from "./ui";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const blank = {
  name: "", slug: "", short_description: "", description: "", flavor_profile: "",
  accent_color: "#FF8C00", image_url: "", price_ex_vat: 0, vat_rate: 21,
  unit_name: "krat (24 flesjes)", minimum_quantity: 1, stock_status: "in_stock",
  sort_order: 0, is_active: true,
};

export default function Products() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/products").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/products/${editing.id}`, editing);
      else await api.post("/admin/products", editing);
      toast.success("Opgeslagen"); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Product verwijderen?")) return; try { await api.delete(`/admin/products/${id}`); toast.success("Verwijderd"); load(); } catch (e) { toast.error(apiErr(e)); } };

  const inp = "w-full rounded-xl border border-white/10 bg-sfc-night px-3 py-2 text-sm text-white focus:border-sfc-orange focus:outline-none";
  const F = (k, label, type = "text") => (
    <label className="block"><span className="mb-1 block text-xs text-white/50">{label}</span>
      <input className={inp} type={type} value={editing[k]} onChange={(e) => setEditing({ ...editing, [k]: type === "number" ? parseFloat(e.target.value || "0") : e.target.value })} /></label>
  );

  return (
    <div>
      <PageHeader title="Producten / smaken" subtitle="Beheer uw drankassortiment">
        <button data-testid="add-product-btn" onClick={() => setEditing({ ...blank })} className="flex items-center gap-2 rounded-full bg-gradient-to-r from-sfc-orange to-sfc-red px-5 py-2.5 text-sm font-semibold text-white"><Plus size={16} /> Nieuwe smaak</button>
      </PageHeader>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {rows.map((p) => (
          <Card key={p.id} className="overflow-hidden" data-testid={`admin-product-${p.slug}`}>
            <div className="h-36 w-full overflow-hidden"><img src={p.image_url} alt={p.name} className="h-full w-full object-cover" /></div>
            <div className="p-5">
              <div className="flex items-center justify-between"><h3 className="font-display text-lg text-white" style={{ color: p.accent_color }}>{p.name}</h3><Badge status={p.is_active ? "approved" : "archived"} /></div>
              <p className="mt-1 text-sm text-white/60">{p.short_description}</p>
              <p className="mt-2 text-sm text-white/80">€{p.price_ex_vat.toFixed(2)} · {p.unit_name}</p>
              <div className="mt-4 flex gap-2">
                <button data-testid={`edit-product-${p.slug}`} onClick={() => setEditing(p)} className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-white/80 hover:bg-white/10"><Pencil size={13} /> Bewerken</button>
                <button onClick={() => del(p.id)} className="flex items-center gap-1.5 rounded-full border border-red-500/20 px-3 py-1.5 text-xs text-red-300 hover:bg-red-500/10"><Trash2 size={13} /> Verwijderen</button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setEditing(null)}>
          <div className="admin-scroll max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-white/10 bg-[#0e0722] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h2 className="font-display text-xl text-white">{editing.id ? "Smaak bewerken" : "Nieuwe smaak"}</h2><button onClick={() => setEditing(null)}><X className="text-white/60" /></button></div>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {F("name", "Naam")}{F("slug", "Slug (optioneel)")}
              {F("flavor_profile", "Smaakprofiel")}{F("accent_color", "Kleur (hex)")}
              {F("price_ex_vat", "Prijs excl. btw", "number")}{F("vat_rate", "Btw %", "number")}
              {F("unit_name", "Verpakkingseenheid")}{F("minimum_quantity", "Min. afname", "number")}
              {F("sort_order", "Sortering", "number")}
              <label className="block"><span className="mb-1 block text-xs text-white/50">Voorraadstatus</span>
                <select className={inp} value={editing.stock_status} onChange={(e) => setEditing({ ...editing, stock_status: e.target.value })}><option value="in_stock">Op voorraad</option><option value="limited">Beperkt</option><option value="out_of_stock">Uitverkocht</option></select></label>
              <label className="block sm:col-span-2"><span className="mb-1 block text-xs text-white/50">Afbeelding URL</span><input className={inp} value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} /></label>
              <label className="block sm:col-span-2"><span className="mb-1 block text-xs text-white/50">Korte omschrijving</span><input className={inp} value={editing.short_description} onChange={(e) => setEditing({ ...editing, short_description: e.target.value })} /></label>
              <label className="block sm:col-span-2"><span className="mb-1 block text-xs text-white/50">Uitgebreide omschrijving</span><textarea rows={3} className={inp} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></label>
              <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="h-4 w-4 accent-sfc-orange" /> Gepubliceerd / actief</label>
            </div>
            <button data-testid="save-product-btn" onClick={save} className="mt-6 w-full rounded-full bg-gradient-to-r from-sfc-orange to-sfc-red px-6 py-3 text-sm font-semibold text-white">Opslaan</button>
          </div>
        </div>
      )}
    </div>
  );
}
